<script setup lang="ts">
import type { ScheduleProgress, ScheduleStepStatus } from '@/utils/scheduleProgress';

/**
 * The segmented bar of a reload or a generate on the player's Schedule tab:
 * Sent → Received → Downloading → Up to date → Reported, with Generating in
 * front for a generate. Done steps are dark, the current step is lime and
 * bold, later steps are pale, and a failed step is red. The one line under it
 * is the progress's detail.
 */
defineProps<{ progress: ScheduleProgress }>();

const SEGMENT_CLASS: Record<ScheduleStepStatus, string> = {
    done: 'bg-foreground/70',
    current: 'bg-lime-400',
    pending: 'bg-foreground/10',
    failed: 'bg-destructive',
};

const LABEL_CLASS: Record<ScheduleStepStatus, string> = {
    done: 'text-foreground',
    current: 'text-foreground font-semibold',
    pending: 'text-muted-foreground/60',
    failed: 'text-destructive font-semibold',
};
</script>

<template>
    <div class="flex flex-col gap-1" data-testid="lzp-schedule-progress">
        <ol class="flex gap-1" :aria-label="`Progress: ${progress.detail}`">
            <li
                v-for="step in progress.steps"
                :key="step.key"
                class="flex min-w-0 flex-1 flex-col gap-1"
                :data-status="step.status"
                :aria-current="step.status === 'current' ? 'step' : undefined"
            >
                <span class="h-1.5 rounded-full" :class="SEGMENT_CLASS[step.status]" aria-hidden="true" />
                <span class="truncate text-[11px] leading-tight" :class="LABEL_CLASS[step.status]">
                    {{ step.label }}
                </span>
            </li>
        </ol>
        <p
            class="text-xs"
            :class="progress.failed ? 'text-destructive' : 'text-muted-foreground'"
            data-testid="lzp-schedule-progress-detail"
        >
            {{ progress.detail }}
        </p>
    </div>
</template>
